import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2 } from 'lucide-react';

const ModalContext = createContext(null);

const DEFAULT_TITLE = 'Notice';

function normalizeTone(message, tone) {
  if (tone) return tone;
  if (typeof message === 'string' && (message.includes('✅') || /success/i.test(message))) {
    return 'success';
  }
  return 'info';
}

export function ModalProvider({ children }) {
  const [modalState, setModalState] = useState(null);

  const closeModal = useCallback(() => {
    setModalState(null);
  }, []);

  const alert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setModalState({
        type: 'alert',
        title: options.title || DEFAULT_TITLE,
        message,
        tone: normalizeTone(message, options.tone),
        resolve
      });
    });
  }, []);

  const confirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setModalState({
        type: 'confirm',
        title: options.title || 'Confirm',
        message,
        tone: options.tone || 'info',
        resolve,
        confirmText: options.confirmText || 'OK',
        cancelText: options.cancelText || 'Cancel'
      });
    });
  }, []);

  const value = useMemo(() => ({ alert, confirm }), [alert, confirm]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      {modalState && (
        <ModalRoot
          {...modalState}
          onClose={closeModal}
        />
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return ctx;
}

function ModalRoot({ type, title, message, tone, resolve, confirmText, cancelText, onClose }) {
  const handleOk = () => {
    resolve(type === 'confirm' ? true : undefined);
    onClose();
  };

  const handleCancel = () => {
    resolve(false);
    onClose();
  };

  const body = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
        {tone === 'success' && (
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#E6F3D5] text-[#88B954]">
            <CheckCircle2 className="h-8 w-8" />
          </div>
        )}
        <h3 className="text-2xl font-semibold text-gray-900">{title}</h3>
        <p className="mt-3 whitespace-pre-line text-sm text-gray-600">{message}</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          {type === 'confirm' && (
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-full border border-gray-200 px-6 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            onClick={handleOk}
            className="rounded-full bg-[#9BCB65] px-8 py-2 text-sm font-semibold text-white hover:bg-[#88B954]"
          >
            {confirmText || 'OK'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
